"use client";

import { type ComponentProps, Fragment, type ReactElement } from "react";
import { Rectangle } from "recharts";

type VizBarShapeProps = ComponentProps<typeof Rectangle>;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Recharts bar を square corners かつ zero-safe に描画する shared shape。
 *
 * @remarks
 * `minPointSize` で 0 値を無理に見せると、「値がある細い bar」のように誤読されやすい。
 * ここでは実際の glyph size が 0 以下の bar を描かず、表示された bar だけが
 * 実データ上の非ゼロ値だと読み取れる状態を優先する。
 *
 * 失敗時挙動:
 * - Recharts から期待する矩形 props が来ない場合は空 Fragment を返して描画しない
 * - 正の幅・高さを持つ glyph だけを角丸なし rectangle として描画する
 */
export function renderSquareZeroSafeBarShape(props: unknown): ReactElement {
  if (typeof props !== "object" || props === null) {
    return <Fragment />;
  }

  const rectangleProps = props as VizBarShapeProps;
  const width = rectangleProps.width;
  const height = rectangleProps.height;

  if (!isFiniteNumber(width) || !isFiniteNumber(height)) {
    return <Fragment />;
  }
  if (width <= 0 || height <= 0) {
    return <Fragment />;
  }

  return <Rectangle {...rectangleProps} radius={0} />;
}
